// First, and before anything that touches @matcha/app-config: it reads
// `process.env` behind a `typeof process` guard that only passes once this
// has run. See env-shim.ts.
import "./env-shim";
import "./index.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";

const root = document.getElementById("root");
if (!root) throw new Error("No #root element in index.html.");

/**
 * `amplify-setup` throws at module scope when the build has no admin pool,
 * and that is deliberate — a sign-in form wired to no pool, or worse to the
 * wrong one, must not fail quietly.
 *
 * But a throw during import means `createRoot` never runs, and the operator
 * gets a WHITE PAGE: no message, no console entry they would think to open,
 * nothing to search for. That is exactly what happened on 4 September 2026,
 * when the console was deployed before its pool ids were pasted into
 * `@matcha/app-config` — the failure was correct and completely mute.
 *
 * The mobile app learned this in August and its `index.js` says so: a
 * module-scope throw there took out `registerComponent` and shipped a black
 * screen. Same lesson, same shape of fix — import defensively, and render
 * the reason.
 *
 * Deliberately NOT a React error boundary: this fires before React exists.
 *
 * A dynamic import with `.then`, not a top-level `await`: the build targets
 * es2020 and browsers without top-level await, and esbuild refuses it
 * outright rather than downlevelling.
 */
void import("./lib/auth/amplify-setup")
  .then(() => {
    createRoot(root).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    showStartupFailure(root, error);
  });

function showStartupFailure(root: HTMLElement, error: unknown): void {
  root.innerHTML = "";
  const panel = document.createElement("main");
  panel.style.cssText =
    "font:14px/1.6 ui-sans-serif,system-ui;max-width:34rem;margin:12vh auto;padding:0 1.5rem;color:#1a1c19";
  const title = document.createElement("h1");
  title.style.cssText = "font-size:1.25rem;margin:0 0 .5rem";
  title.textContent = "This console is not configured";
  const body = document.createElement("p");
  body.style.cssText = "margin:0 0 1rem;color:#4a4f47";
  // `textContent`, never `innerHTML`: the message is ours, but the next
  // one to land here might carry something from a response.
  body.textContent =
    error instanceof Error ? error.message : "Unknown startup failure.";
  const help = document.createElement("p");
  help.style.cssText = "margin:0;color:#4a4f47";
  help.textContent =
    "Nothing is wrong with your account. This build was shipped without " +
    "the Cognito pool it signs in against — see docs/ADMIN_CONSOLE.md.";
  panel.append(title, body, help);
  root.append(panel);
}
